"use client";

import { useId, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import type { TrackPoint } from "@/lib/client-convert";
import {
  type UnitSystem,
  distanceLabel,
  formatDistance,
  formatDuration,
  formatElevation,
  formatPace,
} from "@/lib/units";

type SplitRow = {
  index: number;
  distanceM: number;
  durationMs: number;
  avgHr?: number;
  elevGainM: number;
  partial: boolean;
};

// Linear interpolation of the timestamp at a given cumulative distance between
// two consecutive points, so each split closes exactly on its unit boundary.
function timeAtDistance(a: TrackPoint, b: TrackPoint, targetDist: number): number {
  const da = a.dist ?? 0;
  const db = b.dist ?? 0;
  if (db === da) return b.timeMs;
  const frac = (targetDist - da) / (db - da);
  return a.timeMs + frac * (b.timeMs - a.timeMs);
}

export function computeSplits(points: TrackPoint[], system: UnitSystem): SplitRow[] {
  const unit = system === "imperial" ? 1609.34 : 1000;
  const pts = points.filter((p) => p.dist !== undefined && p.timeMs);
  if (pts.length < 2) return [];

  const baseDist = pts[0].dist ?? 0;
  const rows: SplitRow[] = [];

  let splitIdx = 0;
  let splitStartTime = pts[0].timeMs;
  let hrSum = 0;
  let hrCount = 0;
  let elevGain = 0;
  let prevAlt = pts[0].alt;
  let prevPoint = pts[0];

  for (let i = 1; i < pts.length; i += 1) {
    const p = pts[i];

    if (p.hr !== undefined) {
      hrSum += p.hr;
      hrCount += 1;
    }
    if (p.alt !== undefined && prevAlt !== undefined && p.alt > prevAlt) {
      elevGain += p.alt - prevAlt;
    }
    if (p.alt !== undefined) prevAlt = p.alt;

    const relDist = (p.dist ?? baseDist) - baseDist;
    // A single gap between samples can span more than one unit boundary.
    while (relDist >= (splitIdx + 1) * unit) {
      const boundaryAbs = baseDist + (splitIdx + 1) * unit;
      const boundaryTime = timeAtDistance(prevPoint, p, boundaryAbs);
      rows.push({
        index: splitIdx + 1,
        distanceM: unit,
        durationMs: boundaryTime - splitStartTime,
        avgHr: hrCount ? Math.round(hrSum / hrCount) : undefined,
        elevGainM: elevGain,
        partial: false,
      });
      splitIdx += 1;
      splitStartTime = boundaryTime;
      hrSum = 0;
      hrCount = 0;
      elevGain = 0;
    }

    prevPoint = p;
  }

  // Trailing partial split (anything beyond the last full unit boundary).
  const last = pts[pts.length - 1];
  const partialDist = (last.dist ?? baseDist) - baseDist - splitIdx * unit;
  if (partialDist > 1) {
    rows.push({
      index: splitIdx + 1,
      distanceM: partialDist,
      durationMs: last.timeMs - splitStartTime,
      avgHr: hrCount ? Math.round(hrSum / hrCount) : undefined,
      elevGainM: elevGain,
      partial: true,
    });
  }

  return rows;
}

export function SplitsTable({
  points,
  unitSystem,
}: {
  points: TrackPoint[];
  unitSystem: UnitSystem;
}) {
  const splits = useMemo(() => computeSplits(points, unitSystem), [points, unitSystem]);
  const [open, setOpen] = useState(false);
  const contentId = useId();

  if (splits.length === 0) return null;

  const unitLabel = distanceLabel(unitSystem);
  const showHr = splits.some((s) => s.avgHr !== undefined);
  const showElev = splits.some((s) => s.elevGainM > 0);

  return (
    <div className="rounded-xl p-6 glass-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={contentId}
        className={`w-full flex items-center justify-between gap-4 text-left ${open ? "mb-6" : ""}`}
      >
        <h4 className="font-bold flex items-center gap-2">
          <svg className="size-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="20" x2="4" y2="4" /><line x1="4" y1="20" x2="20" y2="20" /><path d="M4 14l4-4 4 3 5-6" /></svg>
          Splits
        </h4>
        <span className="flex items-center gap-3 text-xs text-muted-foreground">
          {open ? `per ${unitLabel}` : `${splits.length} ${splits.length === 1 ? "split" : "splits"} per ${unitLabel}`}
          <ChevronDown
            className={`size-5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </span>
      </button>
      {open && (
      <div id={contentId} className="overflow-x-auto no-scrollbar">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="pb-2 pr-4 font-bold">{unitLabel}</th>
              <th className="pb-2 pr-4 font-bold">Time</th>
              <th className="pb-2 pr-4 font-bold">Pace</th>
              {showHr && <th className="pb-2 pr-4 font-bold">Avg HR</th>}
              {showElev && <th className="pb-2 font-bold">Elev</th>}
            </tr>
          </thead>
          <tbody>
            {splits.map((s) => (
              <tr key={s.index} className="border-t border-border/60">
                <td className="py-2 pr-4 font-medium tabular-nums">
                  {s.index}
                  {s.partial && (
                    <span className="ml-1 text-xs text-muted-foreground">
                      ({formatDistance(s.distanceM, unitSystem)})
                    </span>
                  )}
                </td>
                <td className="py-2 pr-4 tabular-nums">{formatDuration(s.durationMs)}</td>
                <td className="py-2 pr-4 tabular-nums">
                  {formatPace(s.durationMs, s.distanceM, unitSystem)}
                </td>
                {showHr && (
                  <td className="py-2 pr-4 tabular-nums">
                    {s.avgHr !== undefined ? `${s.avgHr} bpm` : "—"}
                  </td>
                )}
                {showElev && (
                  <td className="py-2 tabular-nums">
                    {s.elevGainM > 0 ? `+${formatElevation(s.elevGainM, unitSystem)}` : "—"}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
